import React from "react";
import { cn } from "../../utils/tailwindUtils";
import { NON_METRIC_KEYS } from "./constants";

/**
 * Renders the structured `data` payload that accompanies every copilot answer
 * as compact metric tiles beneath the prose.
 *
 * The backend returns one domain section per answer, whose shape varies
 * (ai/services/context.py). Rather than hard-coding a layout per domain, this
 * flattens the payload one level:
 *   - scalars                     -> a tile
 *   - flat objects of scalars      -> a titled group of tiles (e.g. available_by_type)
 *   - arrays of objects            -> a titled group using each row's first two fields
 * Anything deeper is skipped, since the prose already summarises it.
 */

const prettifyKey = (key) =>
    String(key)
        .replace(/_/g, " ")
        .replace(/\bpct\b/gi, "%")
        .replace(/\bytd\b/gi, "YTD")
        .replace(/\bctc\b/gi, "CTC")
        .replace(/\btds\b/gi, "TDS")
        .replace(/\blop\b/gi, "LOP")
        .replace(/\bid\b/gi, "ID")
        .trim();

const formatValue = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
    if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
    return String(value);
};

const isScalar = (value) =>
    value === null ||
    ["string", "number", "boolean"].includes(typeof value) ||
    (Array.isArray(value) && value.every((item) => ["string", "number"].includes(typeof item)));

const MetricTile = ({ label, value, compact }) => (
    <div className="rounded-lg border border-border bg-muted/40 px-2.5 py-2">
        <p
            className={cn(
                "truncate uppercase tracking-wide text-muted-foreground",
                compact ? "text-[9px]" : "text-[10px]"
            )}
            title={label}
        >
            {label}
        </p>
        <p
            className={cn(
                "mt-0.5 font-semibold text-foreground",
                compact ? "text-xs" : "text-[13px]"
            )}
        >
            {value}
        </p>
    </div>
);

const TileGroup = ({ title, children, compact }) => (
    <div className="space-y-1.5">
        {title && (
            <p className="text-[10px] font-semibold uppercase tracking-wide text-sidebar-primary dark:text-teal-300">
                {title}
            </p>
        )}
        <div
            className={cn(
                "grid gap-1.5",
                compact
                    ? "grid-cols-2"
                    : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4"
            )}
        >
            {children}
        </div>
    </div>
);

/**
 * Build the render plan from a raw data payload.
 * @param {object} data  the `data` object from POST /ai/chat/
 * @param {number} limit max tiles per group (keeps a bubble from becoming a table)
 */
export const buildDataSections = (data, limit) => {
    if (!data || typeof data !== "object" || Array.isArray(data)) return [];

    const scalars = [];
    const groups = [];

    Object.entries(data).forEach(([key, value]) => {
        if (NON_METRIC_KEYS.includes(key)) return;
        if (value === null || value === undefined || value === "") return;

        if (isScalar(value)) {
            scalars.push({ label: prettifyKey(key), value: formatValue(value) });
            return;
        }

        if (Array.isArray(value)) {
            const rows = value.filter((row) => row && typeof row === "object").slice(0, limit);
            if (!rows.length) return;
            groups.push({
                title: prettifyKey(key),
                tiles: rows.map((row) => {
                    const entries = Object.entries(row).filter(([, v]) => isScalar(v));
                    const [labelEntry, valueEntry] = entries;
                    return {
                        label: formatValue(labelEntry?.[1]) || prettifyKey(labelEntry?.[0] || ""),
                        value: valueEntry ? formatValue(valueEntry[1]) : "—",
                    };
                }),
            });
            return;
        }

        // Plain object of scalars, e.g. available_by_type / earnings_breakup.
        const entries = Object.entries(value).filter(([, v]) => isScalar(v));
        if (!entries.length) return;
        groups.push({
            title: prettifyKey(key),
            tiles: entries.slice(0, limit).map(([k, v]) => ({
                label: prettifyKey(k),
                value: formatValue(v),
            })),
        });
    });

    const sections = [];
    if (scalars.length) sections.push({ title: null, tiles: scalars.slice(0, limit) });
    return sections.concat(groups);
};

const CopilotDataCards = ({ data, compact = false, limit = compact ? 4 : 8 }) => {
    const sections = buildDataSections(data, limit);
    if (!sections.length) return null;

    return (
        <div className="mt-3 space-y-2.5 rounded-lg border border-border bg-card p-2.5">
            {sections.map((section, index) => (
                <TileGroup key={index} title={section.title} compact={compact}>
                    {section.tiles.map((tile, tileIndex) => (
                        <MetricTile
                            key={tileIndex}
                            label={tile.label}
                            value={tile.value}
                            compact={compact}
                        />
                    ))}
                </TileGroup>
            ))}
        </div>
    );
};

export default CopilotDataCards;
